import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Badge } from '@paige/ui';
import type { AccountHit } from './pb-types';
import { channelWord, companiesWord } from './pb-words';
import './partner-billing.css';

export interface AccountPickerProps {
  label: string;
  /** The account chosen, or null. */
  value: AccountHit | null;
  onChange: (account: AccountHit | null) => void;
  /** Asks the server (`GET /v1/admin/partner-accounts?q=`): one page of accounts by name or Zoho id. */
  search: (q: string) => Promise<AccountHit[]>;
  debounceMs?: number;
}

/** The server's answer for what is typed, asked once typing rests; a slower earlier answer never wins. */
function useHits(search: AccountPickerProps['search'], q: string, open: boolean, ms: number) {
  const [hits, setHits] = useState<AccountHit[] | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    const timer = setTimeout(
      () =>
        void search(q.trim()).then(
          (rows) => current && setHits(rows),
          () => current && setHits([]),
        ),
      q ? ms : 0,
    );
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [search, q, open, ms]);
  return hits;
}

/**
 * Find a paying account by name or Zoho id, on the server, so it works at thousands of partners: a combobox
 * with its list of matches, arrow keys and Enter to choose, Escape to close.
 */
export function AccountPicker(props: AccountPickerProps): ReactNode {
  const id = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const hits = useHits(props.search, q, open, props.debounceMs ?? 200) ?? [];
  const choose = (hit: AccountHit | undefined): void => {
    if (!hit) return;
    props.onChange(hit);
    setOpen(false);
    setQ('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, Math.max(hits.length - 1, 0)));
    else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
    else if (e.key === 'Enter' && open) choose(hits[active]);
    else if (e.key === 'Escape') setOpen(false);
    else return;
    e.preventDefault();
  };
  if (props.value)
    return <Chosen label={props.label} hit={props.value} onClear={() => props.onChange(null)} />;
  const typing = (v: string) => (setQ(v), setOpen(true), setActive(0));
  const box = { id, q, open, active: open && hits[active] ? `${id}-${active}` : undefined };
  return (
    <div className="pb-picker">
      <label className="pui-field__label" htmlFor={`${id}-q`}>
        {props.label}
      </label>
      <SearchBox box={box} onType={typing} onKey={onKey} onOpen={setOpen} />
      {open ? <HitList id={id} hits={hits} active={active} onChoose={choose} /> : null}
    </div>
  );
}

function SearchBox(p: {
  box: { id: string; q: string; open: boolean; active: string | undefined };
  onType: (v: string) => void;
  onKey: (e: KeyboardEvent<HTMLInputElement>) => void;
  onOpen: (open: boolean) => void;
}): ReactNode {
  return (
    <input
      id={`${p.box.id}-q`}
      className="pui-input"
      role="combobox"
      aria-expanded={p.box.open}
      aria-controls={`${p.box.id}-list`}
      aria-autocomplete="list"
      aria-activedescendant={p.box.active}
      placeholder="Name or Zoho account id"
      value={p.box.q}
      onFocus={() => p.onOpen(true)}
      onChange={(e) => p.onType(e.target.value)}
      onKeyDown={p.onKey}
      onBlur={() => setTimeout(() => p.onOpen(false), 150)}
    />
  );
}

function HitList(p: {
  id: string;
  hits: AccountHit[];
  active: number;
  onChoose: (h: AccountHit) => void;
}) {
  return (
    <ul className="pb-picker__list" role="listbox" id={`${p.id}-list`} aria-label="Paying accounts">
      {p.hits.length === 0 ? <li className="pb-picker__none">No paying account matches.</li> : null}
      {p.hits.map((h, i) => (
        <li
          key={h.zohoAccountId}
          id={`${p.id}-${i}`}
          role="option"
          aria-selected={i === p.active}
          className="pb-picker__hit"
          onMouseDown={(e) => (e.preventDefault(), p.onChoose(h))}
        >
          <span className="pb-picker__name">{h.name}</span>
          <span className="pb-picker__note">
            {h.zohoAccountId} · {channelWord[h.invoiceChannel]} · {companiesWord(h.companies)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Chosen(p: { label: string; hit: AccountHit; onClear: () => void }): ReactNode {
  return (
    <div className="pb-picker">
      <span className="pui-field__label">{p.label}</span>
      <div className="pb-picker__chosen">
        <strong>{p.hit.name}</strong>
        <Badge tone={p.hit.invoiceChannel === 'quickbooks' ? 'accent' : 'neutral'}>
          {channelWord[p.hit.invoiceChannel]}
        </Badge>
        <button
          type="button"
          className="pb-link"
          aria-label="Change paying account"
          onClick={p.onClear}
        >
          Change
        </button>
      </div>
    </div>
  );
}
